import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Box, Flex } from 'reflexbox';

import {
	Button,
	CardList,
	Container,
	Heading,
	Jumbo,
	Lazy,
	Separator,
	Skeleton,
} from '@treams/treams-ui';
import { Route, Switch } from '@treams/treams-routes';
import { AddIcon, GridIcon } from '@treams/treams-icons';
import { Switch as SwitchButton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';

import TalentReviewCard from './TalentReviewCard';
import CreateTalentReviewModal from '../packages/kakapo-ui-application/pages/talent-reviews/CreateTalentReviewModal';
import GroupSelector from '../goals/group-selector/GroupSelector';

type RouteParams = { id: string };

function TalentReviewSection(props: { group: graphql.Group; isArchived?: boolean }) {
	const viewer = useAuthenticatedUser();
	const { t } = useTranslation();

	const query = graphql.useTalentReviewsQuery({
		variables: { id: props.group.id, isArchived: props.isArchived ?? false },
	});

	return (
		<>
			<Container mt={4}>
				<Flex alignItems="center" justifyContent="space-between">
					<Heading>{props.group.name}</Heading>
					<Button
						icon={<AddIcon />}
						link={`/talent-reviews/${props.group.type.toLowerCase()}/${props.group.id}/new`}
						small
						title={t('talentReviews.index.toolbar.new')}
					/>
				</Flex>
			</Container>
			<Box mt={-3}>
				<CardList
					translationName="talentReviews"
					icon={<GridIcon />}
					query={query}
					path="group.talentReviews"
					skeletonRepeat={2}
					renderCard={(item: graphql.TalentReview) => (
						<TalentReviewCard userID={viewer.id} talentReview={item} />
					)}
					renderSkeleton={() => <TalentReviewCard.Skeleton />}
				/>
			</Box>
		</>
	);
}

TalentReviewSection.Skeleton = function TalentReviewSectionSkeleton() {
	return (
		<Container mt={4}>
			<Heading.Skeleton />
			<Skeleton.Repeat count={10}>
				<TalentReviewCard.Skeleton />
			</Skeleton.Repeat>
		</Container>
	);
};

export default function TalentReviews() {
	const { t } = useTranslation();

	const [selectedGroups, setSelectedGroups] = useState<graphql.Group[]>([]);
	const [showArchived, setShowArchived] = useState(false);

	return (
		<>
			<Jumbo
				title={t('talent-reviews.index.title')}
				description={t('talent-reviews.index.description')}
				footer={<Separator />}
			/>

			<Container mt={4}>
				<GroupSelector
					remember
					canSelect={(group) => group.viewerCanViewTalentReviews}
					onChange={(newGroups) => setSelectedGroups(newGroups)}
				/>

				<Box mt={4}>
					<SwitchButton
						label={t('talentReviews.list.archived.button')}
						on={showArchived}
						onChange={(on) => setShowArchived(on)}
					/>
				</Box>
			</Container>

			{selectedGroups.map((group) => (
				<Lazy key={group.id}>
					<TalentReviewSection group={group} isArchived={showArchived} />
				</Lazy>
			))}

			<Switch>
				<Route<RouteParams> path="/talent-reviews/department/:id/new" exact>
					{({ match }) =>
						match && (
							<CreateTalentReviewModal
								type="department"
								groupID={match.params.id!}
								returnUrl="/talent-reviews"
							/>
						)
					}
				</Route>
				<Route<RouteParams> path="/talent-reviews/team/:id/new" exact>
					{({ match }) =>
						match && (
							<CreateTalentReviewModal
								type="team"
								groupID={match.params.id!}
								returnUrl="/talent-reviews"
							/>
						)
					}
				</Route>
			</Switch>
		</>
	);
}
